import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  IconButton,
  Divider,
  Typography,
  Button,
  Box,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { createBuilding, updateBuilding } from "../../services/WarehouseSettings";
import { IBuildingRequestDTO } from "../../interfaces/WarehouseSettings";
import { formFieldStyles } from "../../styles/formStyles";
import { useAuth } from "../../auth/AuthProvider";

interface AddEditBuildingProps {
  open: boolean;
  onClose: () => void;
  editData: any;
  onSuccess: (message: string) => void;
}

const AddEditBuilding: React.FC<AddEditBuildingProps> = ({
  open,
  onClose,
  editData,
  onSuccess,
}) => {
  const [formData, setFormData] = useState({
    name: "",
    length: "",
    width: "",
    height: "",
  });
  const [errors, setErrors] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">("success");
  const { getLoginUserInfo } = useAuth();
  const loggedInUser = getLoginUserInfo();
  const WAREHOUSE_ID = loggedInUser?.workspaceId;
  const COMPANY_ID = loggedInUser?.companyId;
  useEffect(() => {
    if (editData) {
      setFormData({
        name: editData.name || "",
        length: editData.lengthMtr || "",
        width: editData.widthMtr || "",
        height: editData.heightMtr || "",
      });
    } else {
      setFormData({
        name: "",
        length: "",
        width: "",
        height: "",
      });
    }
    setErrors({});
  }, [editData, open]);

  const validate = () => {
    const newErrors: any = {};
    if (!formData.name) newErrors.name = "Building name is required";
    if (!formData.length) newErrors.length = "Length is required";
    else if (Number(formData.length) <= 0) newErrors.length = "Length must be greater than 0";
    if (!formData.width) newErrors.width = "Width is required";
    else if (Number(formData.width) <= 0) newErrors.width = "Width must be greater than 0";
    if (!formData.height) newErrors.height = "Height is required";
    else if (Number(formData.height) <= 0) newErrors.height = "Height must be greater than 0";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    if (!COMPANY_ID || !WAREHOUSE_ID) return;

    setLoading(true);
    try {
      const isEdit = Boolean(editData?.id);
      
      const dto: IBuildingRequestDTO = {
        companyId: COMPANY_ID,
        warehouseId: WAREHOUSE_ID,
        name: formData.name,
        lengthMtr: Number(formData.length),
        widthMtr: Number(formData.width),
        heightMtr: Number(formData.height),
      };

      const response = isEdit 
        ? await updateBuilding(editData.id, dto)
        : await createBuilding(dto);

      if (response && response.status === "success") {
        const message = isEdit ? "Building updated successfully" : "Building created successfully";
        onSuccess(message);
        onClose();
      } else {
        const errorMsg = response?.errorResponse?.response?.data?.message || 
                         response?.errorResponse?.message || 
                         `Error ${isEdit ? 'updating' : 'creating'} building`;
        throw new Error(errorMsg);
      }
    } catch (err: any) {
      console.error("Save error:", err);
      setSnackbarMessage(`Error ${editData ? 'updating' : 'creating'} building.`);
      setSnackbarSeverity("error");
      setShowSnackbar(true);
    } finally {
      setLoading(false);
    }
  };
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" PaperProps={{ sx: { borderRadius: "12px", width: "600px" } }} slotProps={{ backdrop: { sx: { backdropFilter: "blur(4px)" } } }}>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 0.5, pt: 1.5, px: 2 }}>
        <Box>
          <Typography sx={{ fontSize: "20px", fontWeight: 600 }}>{editData ? "Edit Building" : "Add Building"}</Typography>
          <Typography variant="body2" color="grey.500" sx={{ fontSize: "12px", mt: 0.3 }}>
            {editData ? "Edit Building" : "Add Building"}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ px: 2, py: 1.5 }}>
        <Grid container spacing={1.5} sx={{ mt: 0 }}>
          <Grid item xs={12}>
            <Typography sx={{ mb: 0.3, fontSize: "14px", fontWeight: 500 }}>Building Name <span style={{ color: "red" }}>*</span></Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="eg. Main Warehouse"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              error={!!errors.name}
              helperText={errors.name}
              sx={{...formFieldStyles}}
            />
          </Grid>

          <Grid item xs={12}>
            <Typography sx={{ mb: 0.3, fontSize: "14px", fontWeight: 500 }}>Dimension (L x W x H m) <span style={{ color: "red" }}>*</span></Typography>
          </Grid>
          
          <Grid item xs={4}>
            <TextField
              fullWidth
              size="small"
              type="number"
              placeholder="12"
              value={formData.length}
              onChange={(e) => setFormData({ ...formData, length: e.target.value })}
              error={!!errors.length}
              helperText={errors.length}
              sx={{ 
                ...formFieldStyles,
                "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button": {
                  WebkitAppearance: "none",
                  margin: 0
                },
                "& input[type=number]": {
                  MozAppearance: "textfield"
                }
              }}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              fullWidth
              size="small"
              type="number"
              placeholder="10"
              value={formData.width}
              onChange={(e) => setFormData({ ...formData, width: e.target.value })}
              error={!!errors.width}
              helperText={errors.width}
              sx={{ 
                ...formFieldStyles,
                "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button": {
                  WebkitAppearance: "none",
                  margin: 0
                },
                "& input[type=number]": {
                  MozAppearance: "textfield"
                }
              }}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              fullWidth
              size="small"
              type="number"
              placeholder="15"
              value={formData.height}
              onChange={(e) => setFormData({ ...formData, height: e.target.value })}
              error={!!errors.height}
              helperText={errors.height}
              sx={{ 
                ...formFieldStyles,
                "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button": {
                  WebkitAppearance: "none",
                  margin: 0
                },
                "& input[type=number]": {
                  MozAppearance: "textfield"
                }
              }}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <Divider sx={{ml:2, mr:2}}/>
      <DialogActions sx={{ ml:1, mr:1, justifyContent: "space-between" }}>
        <Button onClick={onClose} variant="outlined" sx={{ borderRadius: "6px", border: "1px solid", borderColor: "primary.main", boxShadow: "0 0 10.7px 0 rgba(0, 94, 255, 0.20) inset", padding: "8px 16px", textTransform: "none" }}>Cancel</Button>
        <Button onClick={handleSave} disabled={loading} variant="contained" sx={{ borderRadius: "6px", border: "1px solid", borderColor: "primary.main", boxShadow: "0 0 10.7px 0 rgba(0, 0, 0, 0.25) inset", padding: "8px 40px", textTransform: "none" }}>
          {loading ? <CircularProgress size={20} color="inherit" /> : (editData ? "Save" : "Add")}
        </Button>
      </DialogActions>

      <Snackbar 
        open={showSnackbar} 
        autoHideDuration={2000}
        onClose={() => setShowSnackbar(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setShowSnackbar(false)} 
          severity={snackbarSeverity}
          variant="filled"
          sx={{ 
            width: "100%", 
            backgroundColor: `${snackbarSeverity === "success" ? "success.main" : "error.main"} !important`,
            color: "white",
            "& .MuiAlert-icon": { color: "white" },
            "& .MuiAlert-action": { color: "white" }
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default AddEditBuilding;
